/*
 * Pfanner — Free-gift progress (Cluster A, task #6)
 *
 * Reads config from the server-rendered `.pfanner-gift` bar (snippets/gift-progress.liquid),
 * which mirrors shop.metafields.pfanner.gift_config ({threshold, gift_variant_id, enabled}).
 *
 * Responsibilities:
 *  - Keep EVERY progress bar (width + message) in sync with the cart on every cart change.
 *    There can be more than one on a page: the drawer renders one and /cart renders another
 *    inside .cart-summary, so updateBar() iterates all matches (see the note there).
 *  - Auto-ADD the gift variant (qty 1) when the qualifying basis crosses the threshold.
 *  - Auto-REMOVE the gift when the basis drops back below the threshold.
 *
 * Basis = cart.items_subtotal_price (incl. tax on a tax-included store, excl. shipping,
 * excl. Pfand — Pfand is not a cart line) MINUS any gift line already in the cart. Excluding
 * the gift from the basis is the primary loop guard: adding the gift never changes the basis,
 * so add/remove cannot oscillate.
 *
 * GATED: the gift line carries its normal price in the cart until a gift-discount / Cart-Transform
 * Function zeroes it at checkout. Until that Function + a real gift_config exist, this is display +
 * cart-mechanics only, and the placeholder gift price inflates the shown cart total / free-ship bar.
 */
(function () {
  'use strict';

  var SELECTOR = '.pfanner-gift';
  var reconciling = false; // true while an add/remove fetch is in flight (guards re-entrancy)
  var addFailed = false; // once an add returns non-OK (e.g. gift OOS → 422), stop re-attempting this session
  var debounceTimer = null;

  // Normalise any variant id to its trailing digits: numeric ("123"), string ("123"),
  // or GID ("gid://shopify/ProductVariant/123") all reduce to "123". /cart.js always
  // returns numeric variant_id, but gift_config.gift_variant_id may be a GID — so both
  // sides MUST be normalised before comparing, and the id must be normalised before it
  // is POSTed to /cart/add (parseInt on a GID → NaN → id:null → 422).
  var numId = function (v) {
    var m = String(v).match(/\d+$/);
    return m ? m[0] : '';
  };

  function moneyEUR(cents) {
    // de-AT: comma decimal, "€" prefix and NO separator — that is what this store's
    // money_format actually emits (rendered cart: "€63,98"; Liquid `money` on this very bar:
    // "€16,02"). The old prefix carried a non-breaking space, so the amount visibly shifted
    // the moment this script took over the server-rendered bar on load.
    return '€' + (Math.round(cents) / 100).toFixed(2).replace('.', ',');
  }

  // Config lives on the persistent #pfanner-gift-config holder (present even when the cart is
  // empty / the visible bar is not rendered), so the reconciler can subscribe on every page load.
  function getConfig() {
    var el = document.getElementById('pfanner-gift-config');
    if (!el) return null;
    var threshold = parseInt(el.dataset.giftThreshold, 10); // cents
    var variantId = String(el.dataset.giftVariantId || '').trim();
    if (!threshold || threshold <= 0 || !variantId) return null;
    // Fall back to the German only if the attributes are missing (an older theme render);
    // normally both arrive translated for the active locale.
    return {
      threshold: threshold,
      variantId: variantId,
      msgRemaining: el.dataset.giftMsgRemaining || 'Noch __AMT__ bis zum Gratis-Geschenk',
      msgInCart: el.dataset.giftMsgInCart || 'Dein Gratis-Geschenk ist im Warenkorb!',
    };
  }

  function computeBasis(cart, variantId) {
    var want = numId(variantId);
    var basis = cart.items_subtotal_price || 0;
    (cart.items || []).forEach(function (item) {
      if (numId(item.variant_id) === want) basis -= item.final_line_price || 0;
    });
    return basis < 0 ? 0 : basis;
  }

  function findGiftLine(cart, variantId) {
    var want = numId(variantId);
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (numId(items[i].variant_id) === want) {
        return { line: i + 1, key: items[i].key, quantity: items[i].quantity };
      }
    }
    return null;
  }

  function updateBar(cfg, basis, giftPresent) {
    // querySelectorAll, NOT querySelector: since the bar was added to the cart page
    // (sections/main-cart-items.liquid, inside .cart-summary) there are TWO .pfanner-gift
    // elements in the DOM on /cart — the drawer's (theme.liquid renders <cart-drawer> at the
    // top of <body>, so it wins document order) and the page's. Updating only the first left
    // the visible cart-page bar frozen at its server-rendered value after every quantity
    // change. Re-queried on each call because both the drawer and .js-contents get re-rendered.
    var bars = document.querySelectorAll(SELECTOR);
    if (!bars.length) return;
    var pct = cfg.threshold > 0 ? Math.min(100, Math.round((basis * 100) / cfg.threshold)) : 0;
    var reached = basis >= cfg.threshold;
    // Only claim the gift is "im Warenkorb" when the line actually exists — the add can
    // 422 (gift OOS) even though basis >= threshold; in that case keep the progress copy.
    var inCart = reached && giftPresent;

    var remaining = cfg.threshold - basis;
    if (remaining < 0) remaining = 0;

    Array.prototype.forEach.call(bars, function (el) {
      var fill = el.querySelector('[data-gift-fill]');
      if (fill) fill.style.width = pct + '%';

      var track = el.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', pct);

      var msg = el.querySelector('[data-gift-message]');
      if (msg) {
        if (inCart) {
          msg.textContent = cfg.msgInCart;
        } else {
          // The template arrives already translated, with __AMT__ where the amount goes. Bolding
          // happens here so the <b> never has to survive Liquid's HTML escaping into an attribute.
          msg.innerHTML = cfg.msgRemaining.replace('__AMT__', '<span class="pf-b">' + moneyEUR(remaining) + '</span>');
        }
      }
      el.classList.toggle('pfanner-gift--reached', inCart);
    });
  }

  function fetchCart() {
    return fetch((window.routes ? window.routes.cart_url : '/cart') + '.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  // Sections we can render without opening the drawer (mirrors what Dawn touches on a cart update).
  function sectionsToRequest() {
    var list = ['cart-drawer', 'cart-icon-bubble'];
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id) list.push(main.dataset.id);
    return list;
  }

  function renderSections(parsed) {
    var sections = (parsed && parsed.sections) || {};

    // Cart drawer — replace the items table + footer (leaves the progress bars alone; JS syncs them).
    if (sections['cart-drawer']) {
      var ddoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
        var src = ddoc.querySelector(sel);
        var tgt = document.querySelector(sel);
        if (src && tgt) tgt.replaceWith(src);
      });
      // Those two selectors carry the FOOTER's empty-state switch but not the
      // drawer's own, and they miss the sibling `.drawer__inner-empty` block —
      // so a render that crosses the empty <-> not-empty line would leave
      // "Warenkorb ist leer" standing above a real total. Helper + full note in
      // assets/cart.js (loaded first by snippets/cart-drawer.liquid). It adds a
      // MISSING progress-bar block but never replaces a live one, so updateBar()
      // below is not reset under us.
      if (typeof window.pfannerSyncCartDrawerEmptyState === 'function') {
        window.pfannerSyncCartDrawerEmptyState(ddoc);
      }
    }

    // Cart page — swap the <cart-items> inner content, if present.
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id && sections[main.dataset.id]) {
      var mdoc = new DOMParser().parseFromString(sections[main.dataset.id], 'text/html');
      var src = mdoc.querySelector('cart-items');
      var tgt = document.querySelector('cart-items');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    }

    // Header cart-count bubble.
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var inner = bdoc.querySelector('.shopify-section') || bdoc.body;
        bubble.innerHTML = inner.innerHTML;
      }
    }
  }

  function addGift(variantId) {
    return fetch((window.routes ? window.routes.cart_add_url : '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: numId(variantId), quantity: 1 }],
        sections: sectionsToRequest(),
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function changeGift(idOrKey, quantity) {
    return fetch((window.routes ? window.routes.cart_change_url : '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: idOrKey,
        quantity: quantity,
        sections: sectionsToRequest(),
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function evaluate() {
    var cfg = getConfig();
    if (!cfg || reconciling) return;

    // G1: claim the critical section SYNCHRONOUSLY, before the async fetchCart. The old
    // code only set reconciling=true inside the .then (after ~130ms of network), so a
    // second cartUpdate could pass the guard in the gap and double-add. Cleared once in
    // the finally at the tail of the whole chain.
    reconciling = true;

    fetchCart()
      .then(function (cart) {
        if (!cart) return;
        var basis = computeBasis(cart, cfg.variantId);
        var giftLine = findGiftLine(cart, cfg.variantId);
        var shouldHave = basis >= cfg.threshold;

        updateBar(cfg, basis, !!giftLine);

        // G3: only add if the gift isn't already present AND we haven't already had an
        // add fail this session (don't hammer a failing/OOS gift add).
        if (shouldHave && !giftLine && !addFailed) {
          return addGift(cfg.variantId).then(function (state) {
            // /cart/add.js returns the added line (has a `key`) on success, or an error
            // object with a numeric `status` (e.g. 422 when the gift is OOS) on failure.
            var ok = state && !(state.status && state.status >= 400);
            if (!ok) {
              addFailed = true;
              updateBar(cfg, basis, false); // keep "Noch X €"; gift is NOT in the cart
              return;
            }
            if (state.sections) renderSections(state);
            else fetchCart().then(refreshBubbleOnly);
            updateBar(cfg, basis, true); // gift now in the cart
          });
        }

        if (!shouldHave && giftLine) {
          return changeGift(giftLine.key || giftLine.line, 0).then(function (state) {
            renderSections(state);
            updateBar(cfg, basis, false);
          });
        }

        // Normalise an accidental qty > 1 on the gift back to 1.
        if (shouldHave && giftLine && giftLine.quantity > 1) {
          return changeGift(giftLine.key || giftLine.line, 1).then(function (state) {
            renderSections(state);
          });
        }
      })
      .catch(function (e) {
        // Placeholder variant id / network hiccup — keep the display, skip the mutation.
        if (window.console) console.warn('[pfanner-gift]', e);
      })
      .finally(function () {
        reconciling = false;
      });
  }

  // Fallback when /cart/add.js is called without a sections echo (older API): just refresh the badge.
  function refreshBubbleOnly() {
    return fetch((window.routes ? window.routes.cart_url : '/cart') + '?section_id=cart-icon-bubble')
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('.shopify-section') || doc.body;
        bubble.innerHTML = inner.innerHTML;
      })
      .catch(function () {});
  }

  function scheduleEvaluate() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 120);
  }

  function init() {
    if (!getConfig()) return; // gift disabled / not configured → stay inert

    // React to every cart change. Skip our own mutations (we render sections directly, so we
    // deliberately do NOT publish cartUpdate from this module — nothing to skip there — but we
    // still guard the reconciling flag inside evaluate()).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleEvaluate();
      });
    }

    // Reconcile once on load (cart may already qualify, or hold a stale gift).
    scheduleEvaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
